import './AlertsAndNotifications.scss';

import React from 'react';
import { connect } from 'react-redux';
import { isEmpty, isEqual, omit } from 'lodash';

import Content, { contentConverter } from 'services/Content';
import PreferencesSection from 'components/layouts/Preferences/shared/PreferencesSection';
import PreferencesHeader from 'components/layouts/Preferences/PreferencesHeader';
import {
  getAlertsAndNotifications,
  updateAlertsAndNotifications,
  getIsActivatedForTextAlerts,
  updateCustomerPreferences,
  lookupMobileCarrier,
  getSecurityOperationalConsent,
  getMobilePhoneShortCodeDeactivationReason
} from 'services/Rest';
import { ReactComponent as Message } from 'assets/alertsAndNotifications/message.svg';
import { ReactComponent as EmailIcon } from 'assets/alertsAndNotifications/email.svg';
import AlertsAndNotificationsTableRow from 'components/layouts/Preferences/AlertsAndNotifications/AlertsAndNotificationsTableRow';
import { InputField } from '@trp-beacon-ui-kit/react-controls';
import {
  getCustomerContactInformation,
  getCustomerWebView,
  getPhoneNumbers,
  getMobilePhoneNumber,
  getPrimaryEmailAddress
} from 'state/customer/CustomerSelectors';
import AlertsAndNotificationsModal from 'components/layouts/Preferences/AlertsAndNotifications/AlertsAndNotificationsModal';
import {
  componentEmailToAppEmailMapper,
  componentPhoneNumberToAppPhoneNumberMapper,
  isNewPhoneByType
} from 'services/Preferences';
import {
  ALERTS_AND_NOTIFICATIONS_EMAIL,
  ALERTS_AND_NOTIFICATIONS_SMS,
  generateAlertsAndNotificationsOptions,
  generateAlertsAndNotificationsPayload,
  ACCOUNT_INFORMATION,
  MUTUAL_FUND_TRANSACTIONS,
  CONTACT_INFORMATION,
  LOGIN_AND_SECURITY
} from 'services/AlertsAndNotifications';
import {
  isValidEmail,
  isValidRequiredUsPhoneNumber,
  convertValidationToBoolean
} from 'services/Validation';
import {
  getAlertsAndNotificationsSelections,
  getAlertsAndNotificationsEmail,
  retrieveAlertsAndNotifications,
  getAlertsAndNotificationsPhoneNumbers,
  getHasOptedOutOfTextAlerts,
  getHasDeactivationReasonCode,
  getIsActivatedForAlerts,
  getIsEligibleForSMSSignUp
} from 'state/alertsAndNotifications/AlertsAndNotificationsSelectors';
import Store from 'state/Store';
import {
  errorUpdateAlertsAndNotifications,
  updateAlertOptions
} from 'state/alertsAndNotifications/AlertsAndNotificationsActions';
import FixedSubmitFooter from 'components/layouts/Preferences/shared/FixedSubmitFooter/FixedSubmitFooter';
import CancelConfirmationModal from 'components/shared/CancelConfirmationModal/CancelConfirmationModal';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import {
  hideFixedFooterSaveOptions,
  showFixedFooterSaveOptions
} from 'state/preferences/PreferencesActions';
import { ReactComponent as WarningTriangle } from 'assets/icons/warning-triangle.svg';
import LegalCopy from 'components/shared/LegalCopy/LegalCopy';
import {
  extractPhoneNumber,
  getPhoneNumberParts,
  onlyNumbers
} from 'services/Formats';
import {
  DAYTIME,
  EVENING,
  FAX,
  MOBILE
} from 'services/ContactMethodMaintenance';
import NotificationsBrokerageMFE from './NotificationsMFE';
import {hasOwnedBrokerage} from "../../../../state/account/AccountSelectors";

export const createContactInfoUpdatePayload = (
  email,
  emailOnFile,
  phoneNumbers,
  phoneNumbersOnFile,
  contactInfoOnFile
) => {
  const appEmail = componentEmailToAppEmailMapper(email, emailOnFile);
  const appPhoneNumbers = componentPhoneNumberToAppPhoneNumberMapper(
    phoneNumbers,
    phoneNumbersOnFile,
    false
  );

  let contactInfoForPayload = {
    ...contactInfoOnFile,
    primaryEmailAddress: appEmail,
    phoneNumbers: appPhoneNumbers
  };

  if (contactInfoForPayload && contactInfoForPayload['previousEmailAddress']) {
    contactInfoForPayload = omit(contactInfoForPayload, 'previousEmailAddress');
  }

  return {
    contactInformation: contactInfoForPayload
  };
};

export const getFormattedPhoneForUpdate = (
  mobilePhoneNumber,
  phoneNumbersOnFile
) => {
  const formattedObj = {};
  const isAddingNewMobilePhone = isNewPhoneByType(phoneNumbersOnFile, MOBILE);
  const phoneNumberParts = getPhoneNumberParts(mobilePhoneNumber);
  const { areaCode, countryCode, exchange, localNumber } = phoneNumberParts;
  if (isAddingNewMobilePhone) {
    formattedObj.mobilePhoneCountryCode = countryCode || '1';
    formattedObj.mobilePhoneExt = '';
    formattedObj.mobilePhone = `${areaCode}${exchange}${localNumber}`;
  }
  phoneNumbersOnFile.forEach((phone) => {
    if (phone.typeId === MOBILE) {
      formattedObj.mobilePhoneCountryCode = countryCode || '1';
      formattedObj.mobilePhoneExt = '';
      formattedObj.mobilePhone = `${areaCode}${exchange}${localNumber}`;
    }
    if (phone.typeId === DAYTIME) {
      formattedObj.daytimePhoneCountryCode = phone.countryCode || '1';
      formattedObj.daytimePhoneExt = phone.ext || '';
      formattedObj.daytimePhone = `${phone.areaCode}${phone.exchange}${phone.localNumber}`;
    }
    if (phone.typeId === EVENING) {
      formattedObj.eveningPhoneCountryCode = phone.countryCode || '1';
      formattedObj.eveningPhoneExt = phone.ext || '';
      formattedObj.eveningPhone = `${phone.areaCode}${phone.exchange}${phone.localNumber}`;
    }
    if (phone.typeId === FAX) {
      formattedObj.faxNumberCountryCode = phone.countryCode || '1';
      formattedObj.faxNumberExt = phone.ext || '';
      formattedObj.faxNumber = `${phone.areaCode}${phone.exchange}${phone.localNumber}`;
    }
  });
  return formattedObj;
};

export class AlertsAndNotifications extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      disableSubmit: true,
      email: (props.emailOnFile && props.emailOnFile.address) || '',
      phone: props.mobilePhoneNumber || '',
      hasChangedAlertPreferences: false,
      refresh: false,
      isEditingEmail: false,
      isEditingPhone: false,
      showModal: false,
      showConfirmationModal: false
    };
  }

  componentDidMount() {
    const { mobilePhoneNumber, emailOnFile } = this.props;

    if (emailOnFile && emailOnFile.address) {
      this.setState({
        email: emailOnFile.address
      });
    }
    if (mobilePhoneNumber) {
      this.setState({ phone: mobilePhoneNumber });
    }
    if (!isEmpty(mobilePhoneNumber)) {
      const phoneNumber = getPhoneNumberParts(mobilePhoneNumber);
      const mobilePhonePayload = {
        ...phoneNumber,
        typeId: MOBILE
      };
      getIsActivatedForTextAlerts(mobilePhonePayload);
      getMobilePhoneShortCodeDeactivationReason(mobilePhonePayload);
    }
    getSecurityOperationalConsent();
    getAlertsAndNotifications();
    lookupMobileCarrier();

    Store.dispatch(hideFixedFooterSaveOptions());
  }

  componentDidUpdate(prevProps, prevState) {
    const { mobilePhoneNumber, emailOnFile } = this.props;

    if (
      (prevState.refresh !== this.state.refresh && this.state.refresh) ||
      prevProps.mobilePhoneNumber !== mobilePhoneNumber
    ) {
      if (!isEmpty(mobilePhoneNumber)) {
        const phoneNumber = getPhoneNumberParts(mobilePhoneNumber);
        const mobilePhonePayload = {
          ...phoneNumber,
          typeId: MOBILE
        };
        getIsActivatedForTextAlerts(mobilePhonePayload);
        getMobilePhoneShortCodeDeactivationReason(mobilePhonePayload);
      }
      getSecurityOperationalConsent();
      getAlertsAndNotifications();
      this.setState({ refresh: false });
    }

    if (
      prevProps.emailOnFile &&
      emailOnFile &&
      prevProps.emailOnFile.address !== emailOnFile.address
    ) {
      if (emailOnFile.address) {
        this.setState({
          email: emailOnFile.address
        });
      }
    }
    if (prevProps.mobilePhoneNumber !== mobilePhoneNumber) {
      this.setState({ phone: mobilePhoneNumber });
    }

    if (
      (!isEmpty(this.state.email) && prevState.email !== this.state.email) ||
      onlyNumbers(prevState.phone) !== onlyNumbers(this.state.phone) ||
      !isEqual(
        prevProps.alertsAndNotificationsSelections,
        this.props.alertsAndNotificationsSelections
      ) ||
      prevProps.hideFixedFooterSaveOptions !==
        this.props.hideFixedFooterSaveOptions
    ) {
      const disableSubmit = this.shouldDisableSave();
      this.setState({ disableSubmit });
    }
  }

  reloadRegistrationData = () => {
    this.setState({ refresh: true });
  };

  handleEditEmail = () => {
    this.setState({ isEditingEmail: true });
    Store.dispatch(showFixedFooterSaveOptions());
  };

  handleEditPhone = () => {
    this.setState({ isEditingPhone: true });
    Store.dispatch(showFixedFooterSaveOptions());
  };

  handleClearEdits = () => {
    this.setState({
      isEditingEmail: false,
      isEditingPhone: false,
      hasChangedAlertPreferences: false
    });
  };

  handleShowModal = () => {
    this.setState({ showModal: true });
  };

  handleCloseModal = (refreshData = false) => {
    this.setState({ showModal: false });
    if (refreshData) {
      this.reloadRegistrationData();
    }
  };

  onChangeEmail = (event) => {
    if (event && event.target.value) {
      const target = event.target;
      const cursorPosition = target.selectionStart;
      const value = event.target.value.trim();
      this.setState({ email: value });
      target.selectionEnd = cursorPosition;
    } else {
      this.setState({ email: '' });
    }
  };

  onChangePhone = (event) => {
    if (event && event.target.value) {
      const target = event.target;
      const cursorPosition = target.selectionStart;
      target.value = onlyNumbers(event.target.value);
      this.setState({ phone: target.value });
      target.selectionEnd = cursorPosition;
    } else {
      this.setState({ phone: '' });
    }
  };

  handleRadioChange = (value, field) => {
    if (value && field) {
      Store.dispatch(updateAlertOptions(field, value));
      Store.dispatch(showFixedFooterSaveOptions());
      this.setState({ hasChangedAlertPreferences: true });
    }
  };

  shouldDisableSave = () => {
    const { phone, email } = this.state;
    const {
      alertsAndNotificationsSelections,
      alertsAndNotifications,
      emailAddress,
      phoneNumbers
    } = this.props;
    const { email: emailOfRecord } = emailAddress || {};
    const { mobilePhone } = phoneNumbers || [];
    if (isEmpty(phone) || isEmpty(email)) {
      return true;
    }

    const emailChanged = email !== emailOfRecord;
    const phoneChanged = onlyNumbers(phone) !== onlyNumbers(mobilePhone);

    if (emailChanged) {
      return !convertValidationToBoolean(isValidEmail(email));
    }
    if (phoneChanged) {
      const isValid = convertValidationToBoolean(
        isValidRequiredUsPhoneNumber(onlyNumbers(phone))
      );
      return !isValid;
    }
    const existingSelections = alertsAndNotifications
      .filter((a) => a.businessLineId === 2)
      .map((fullAlert) => {
        return [
          fullAlert.activityAlertCategoryType,
          fullAlert.contactMethods.some(
            (cm) => cm.activityAlertContactMethodType === 'SMS'
          )
            ? ALERTS_AND_NOTIFICATIONS_SMS
            : ALERTS_AND_NOTIFICATIONS_EMAIL
        ];
      });

    const currentSelectionEntries = Object.entries(
      alertsAndNotificationsSelections
    );

    return currentSelectionEntries.every((cse) => {
      const es = existingSelections.find((e) => e[0] === cse[0]);
      return isEqual(cse, es);
    });
  };

  handleSubmitCancelConfirmation = () => {
    const { history } = this.props;
    history.push('/profile');
  };

  handleSubmitCancel = () => {
    this.setState({ showConfirmationModal: true });
  };

  handleCancelConfirmation = () => {
    this.setState({ showConfirmationModal: false });
  };

  handleSave = () => {
    const {
      isEditingEmail,
      isEditingPhone,
      hasChangedAlertPreferences,
      email: componentEmail,
      phone: componentPhone
    } = this.state;
    const {
      alertsAndNotificationsSelections,
      alertsAndNotifications,
      phoneNumbersOnFile,
      contactInformation,
      emailOnFile
    } = this.props;

    if (isEditingEmail || isEditingPhone) {
      const formattedComponentEmail = { email: componentEmail };
      const formattedComponentPhone = getFormattedPhoneForUpdate(
        componentPhone,
        phoneNumbersOnFile
      );
      const updatePreferencesPayload = createContactInfoUpdatePayload(
        formattedComponentEmail,
        emailOnFile,
        formattedComponentPhone,
        phoneNumbersOnFile,
        contactInformation
      );
      updateCustomerPreferences(
        updatePreferencesPayload,
        () => {
          this.handleClearEdits();
          this.reloadRegistrationData();
        },
        () => {
          Store.dispatch(errorUpdateAlertsAndNotifications());
        }
      );
    }

    if (hasChangedAlertPreferences) {
      const payload = generateAlertsAndNotificationsPayload(
        alertsAndNotificationsSelections,
        alertsAndNotifications
      );
      updateAlertsAndNotifications(payload);
    }
  };

  render() {
    const {
      isActivatedForAlerts,
      alertsAndNotificationsSelections,
      phoneNumbers,
      mobilePhoneNumber,
      hideFixedFooterSaveOptions,
      eligibleForSMSSignUp,
      hasDeactivationReasonCode,
      isCustomerWebView,
      ownsBrokerage
    } = this.props;

    const {
      disableSubmit,
      isEditingEmail,
      isEditingPhone,
      showModal,
      showConfirmationModal,
      email,
      phone
    } = this.state;

    const phoneNumber = phoneNumbers && phoneNumbers.mobilePhone;
    return (
      <div className="alerts-and-notifications-container">
        <FixedSubmitFooter
          cancel={this.handleSubmitCancel}
          requestStatusKey={
            isEditingEmail || isEditingPhone
              ? 'updateCustomerPreferences'
              : 'updateAlertsAndNotifications'
          }
          shouldShow={!hideFixedFooterSaveOptions}
          shouldDisableSubmit={disableSubmit}
          submit={this.handleSave}
        />

        <CancelConfirmationModal
          cancelPrompt="You haven’t saved your changes."
          cancelTitle="Are you sure you want to leave?"
          onCancel={this.handleCancelConfirmation}
          onContinue={this.handleSubmitCancelConfirmation}
          operationName="updateAlertsAndNotifications"
          showModal={showConfirmationModal}
          continueText="Yes, Leave"
          cancelText="No, Keep Editing"
        />
        <PreferencesHeader>
          <div className="alerts-and-notifications-heading">
            Alerts & Notifications
          </div>
        </PreferencesHeader>
        <div className="account-activity-alerts">
          Account activity alerts
        </div>
        <PreferencesSection
          className="alerts-and-notifications-preferences-section"
          fullBackground={true}>
          <div className="alerts-and-notifications-contact-info-container">
            <div className="alerts-and-notifications-contact-info">
              Contact Information
            </div>
            <div>
              <div className="alerts-and-notifications-input-container">
                <Message className="alerts-and-notifications-icon" />
                <span className="contact-label">Mobile: </span>
                <InputField
                  data-testid={'phone-input'}
                  name="phone-input"
                  className="phone-number-input"
                  format={'us'}
                  onChange={this.onChangePhone}
                  disabled={!isEditingPhone || isCustomerWebView}
                  validation={[isValidRequiredUsPhoneNumber]}
                  value={phone}
                />
                {!isEditingPhone && (
                  <span>
                    <button
                      className="alerts-and-notifications-edit-button"
                      onClick={this.handleEditPhone}>
                      Edit
                    </button>
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="alerts-and-notifications-input-container">
                <EmailIcon className="alerts-and-notifications-icon" />
                <span className="contact-label">Email: </span>
                <InputField
                  className="email-input"
                  name="email-input"
                  data-testid={'email-input'}
                  format={'us'}
                  onChange={this.onChangeEmail}
                  disabled={!isEditingEmail || isCustomerWebView}
                  validation={[isValidEmail]}
                  value={email}
                />
                {!isEditingEmail && (
                  <span>
                    <button
                      className="alerts-and-notifications-edit-button"
                      onClick={this.handleEditEmail}>
                      {isEmpty(mobilePhoneNumber) ? 'Add' : 'Edit'}
                    </button>
                  </span>
                )}
              </div>
            </div>
            <div className="alerts-and-notifications-legal-copy">
              <LegalCopy
                content={Content.alertsAndNotificationsFooterDisclosure}
              />
            </div>
          </div>

          <div className="alerts-and-notifications-separator"></div>

          <div className="alerts-and-notifications-activity-alerts-container">
            <div className="alerts-and-notifications-activity-alerts">
              {eligibleForSMSSignUp && (
                <span>
                  <button
                    className="alerts-and-notifications-button"
                    disabled={
                      isEmpty(mobilePhoneNumber) ||
                      isEditingPhone ||
                      isEditingEmail
                    }
                    onClick={this.handleShowModal}>
                    Sign Up for Text Alerts
                  </button>
                </span>
              )}
            </div>
            {!isActivatedForAlerts && !isEmpty(mobilePhoneNumber) && (
              <div className="alerts-and-notifications-activity-alerts-sub">
                <span
                  dangerouslySetInnerHTML={{
                    __html: contentConverter.makeHtml(
                      Content.alertsAndNotificationsIntro
                    )
                  }}
                />
              </div>
            )}
            {hasDeactivationReasonCode && (
              <div className="alerts-and-notifications-deactivation-text">
                <WarningTriangle className="warning-triangle" />{' '}
                <span
                  dangerouslySetInnerHTML={{
                    __html: contentConverter.makeHtml(
                      Content.alertsAndNotificationsDeactivationDisclaimer
                    )
                  }}
                />
              </div>
            )}
            {!isActivatedForAlerts && isEmpty(mobilePhoneNumber) && (
              <div className="alerts-and-notifications-activity-alerts-sub">
                Please add a valid phone number to your profile.
              </div>
            )}
            {!eligibleForSMSSignUp && (
              <div>
                <div className="alerts-and-notifications-activity-alerts-sub">
                  Select the delivery method for each Account Activity Alert.
                  For added security we always send alerts via email.
                </div>
                <div className="alerts-and-notifications-activity-alerts-table">
                  <div className="alerts-and-notifications-activity-alerts-table-heading">
                    <div className="alerts-and-notifications-toggle-headers">
                      <div className="alerts-and-notifications-activity-alerts-table-heading-text">
                        Email
                      </div>
                      <div className="alerts-and-notifications-activity-alerts-table-heading-text">
                        Email & Text
                      </div>
                    </div>
                  </div>
                  <div className="alerts-and-notifications-activity-alerts-table-body">
                    <AlertsAndNotificationsTableRow
                      label="Login & Security Information"
                      selection={
                        alertsAndNotificationsSelections &&
                        alertsAndNotificationsSelections[LOGIN_AND_SECURITY]
                      }
                      options={generateAlertsAndNotificationsOptions(
                        LOGIN_AND_SECURITY
                      )}
                      name={LOGIN_AND_SECURITY}
                      onChange={(value) =>
                        this.handleRadioChange(value, LOGIN_AND_SECURITY)
                      }
                      content={Content.alertsAndNotificationsLogin}
                    />
                    <AlertsAndNotificationsTableRow
                      label="Contact Information"
                      selection={
                        alertsAndNotificationsSelections &&
                        alertsAndNotificationsSelections[CONTACT_INFORMATION]
                      }
                      options={generateAlertsAndNotificationsOptions(
                        CONTACT_INFORMATION
                      )}
                      name={CONTACT_INFORMATION}
                      onChange={(value) =>
                        this.handleRadioChange(value, CONTACT_INFORMATION)
                      }
                      content={Content.alertsAndNotificationsContactInfo}
                    />
                    <AlertsAndNotificationsTableRow
                      label="Online Mutual Fund Transactions"
                      selection={
                        alertsAndNotificationsSelections &&
                        alertsAndNotificationsSelections[
                          MUTUAL_FUND_TRANSACTIONS
                        ]
                      }
                      options={generateAlertsAndNotificationsOptions(
                        MUTUAL_FUND_TRANSACTIONS
                      )}
                      name={MUTUAL_FUND_TRANSACTIONS}
                      onChange={(value) =>
                        this.handleRadioChange(value, MUTUAL_FUND_TRANSACTIONS)
                      }
                      content={Content.alertsAndNotificationsMF}
                    />
                    <AlertsAndNotificationsTableRow
                      label="Account Information"
                      selection={
                        alertsAndNotificationsSelections &&
                        alertsAndNotificationsSelections[ACCOUNT_INFORMATION]
                      }
                      options={generateAlertsAndNotificationsOptions(
                        ACCOUNT_INFORMATION
                      )}
                      name={ACCOUNT_INFORMATION}
                      onChange={(value) =>
                        this.handleRadioChange(value, ACCOUNT_INFORMATION)
                      }
                      content={Content.alertsAndNotificationsAccountInfo}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="b-typography-sans-serif--caption--200">
              Text message and data rates may apply. You can text STOP to cancel
              or HELP for help anytime. Message frequency varies. Please see our{' '}
              <a
                href={
                  'https://www.troweprice.com/personal-investing/help/policies-and-security/text-message-agreement.html'
                }
                className="app-link"
                rel="noreferrer"
                target="_blank">
                <span className="b-typography-sans-serif--caption--200">Text Message Agreement</span>
              </a>{' '}
              and our{' '}
              <a
                className="app-link"
                rel="noreferrer"
                href={
                  'https://www.troweprice.com/enterprise/legal/us/en/privacy-notices.html'
                }
                target="_blank">
                <span className="b-typography-sans-serif--caption--200">Privacy Notice</span>
              </a>
              . Personal data collection through the short code/SMS program will{' '}
              not be shared, sold, or rented to unaffiliated third parties for their{' '}
              own marketing purposes.
            </div>
          </div>
        </PreferencesSection>
        {ownsBrokerage && (
          <>
            <div className="brokerage-account-activity-alerts">
              Brokerage account activity alerts
            </div>
            <div>
              <NotificationsBrokerageMFE />
            </div>
          </>
        )}
        {showModal && (
          <AlertsAndNotificationsModal
            phoneNumber={phoneNumber}
            handleCloseModal={this.handleCloseModal}
          />
        )}
      </div>
    );
  }
}

AlertsAndNotifications.defaultProps = {};

const mapStateToProps = (state) => ({
  isCustomerWebView: getCustomerWebView(state),
  contactInformation: getCustomerContactInformation(state),
  mobilePhoneNumber: extractPhoneNumber(getMobilePhoneNumber(state)),
  emailAddress: getAlertsAndNotificationsEmail(state),
  emailOnFile: getPrimaryEmailAddress(state),
  phoneNumbers: getAlertsAndNotificationsPhoneNumbers(state),
  phoneNumbersOnFile: getPhoneNumbers(state),
  alertsAndNotificationsSelections: getAlertsAndNotificationsSelections(state),
  alertsAndNotifications: retrieveAlertsAndNotifications(state),
  eligibleForSMSSignUp: getIsEligibleForSMSSignUp(state),
  isActivatedForAlerts: getIsActivatedForAlerts(state),
  requestStatus: getRequestStatus(state),
  hideFixedFooterSaveOptions: state.hideFixedFooterSaveOptions,
  hasOptedOutOfTextAlerts: getHasOptedOutOfTextAlerts(state),
  hasDeactivationReasonCode: getHasDeactivationReasonCode(state),
  ownsBrokerage: hasOwnedBrokerage(state)

});

export default connect(mapStateToProps)(AlertsAndNotifications);
